import { useEffect } from 'react'

interface SEOHeadProps {
  title?: string
  description?: string
  keywords?: string
  image?: string
  url?: string
  type?: 'website' | 'article' | 'course' | 'profile'
  author?: string
  publishedTime?: string
  modifiedTime?: string
  section?: string
  noindex?: boolean
  canonical?: string
  structuredData?: object
  breadcrumbs?: Array<{ name: string; url: string }>
  course?: {
    name: string
    description: string
    price: number
    currency: string
    duration: string
    level: string
  }
}

const BASE_URL = 'https://sudhasacademy.com'
const DEFAULT_IMAGE = `${BASE_URL}/Hero.png`
const SITE_NAME = "Sudha's Academy"

export default function SEOHead({
  title,
  description = "India's premier beauty and makeup training institute with 18+ years of excellence. Professional courses in bridal makeup, skincare, hair styling, and cosmetology.",
  keywords = "makeup academy, beauty training, bridal makeup course, cosmetology diploma, hair styling, skincare training, Sudha's Academy",
  image = DEFAULT_IMAGE,
  url = BASE_URL,
  type = 'website',
  author = 'Dr. Sudha M',
  publishedTime,
  modifiedTime,
  section,
  noindex = false,
  canonical,
  structuredData,
  breadcrumbs,
  course
}: SEOHeadProps) {
  const fullTitle = title 
    ? `${title} | ${SITE_NAME}` 
    : `${SITE_NAME} | Best Makeup & Beauty Training Institute in India`

  useEffect(() => {
    // Update document title
    document.title = fullTitle

    // Update meta tags dynamically
    updateMetaTag('description', description)
    updateMetaTag('keywords', keywords)
    updateMetaTag('author', author)

    // Open Graph tags
    updateMetaTag('og:title', fullTitle, 'property')
    updateMetaTag('og:description', description, 'property')
    updateMetaTag('og:image', image, 'property')
    updateMetaTag('og:url', url, 'property')
    updateMetaTag('og:type', type, 'property')

    // Twitter tags
    updateMetaTag('twitter:title', fullTitle)
    updateMetaTag('twitter:description', description)
    updateMetaTag('twitter:image', image)

    // Robots
    if (noindex) {
      updateMetaTag('robots', 'noindex, nofollow')
    } else {
      updateMetaTag('robots', 'index, follow, max-image-preview:large')
    }

    // Canonical URL
    updateCanonical(canonical || url)

    // Add structured data if provided
    if (structuredData) {
      addStructuredData('page-structured-data', structuredData)
    }

    // Add breadcrumb structured data
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
        }))
      }
      addStructuredData('breadcrumb-structured-data', breadcrumbData)
    }

    // Add course structured data
    if (course) {
      const courseData = {
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: course.name,
        description: course.description,
        provider: {
          '@type': 'EducationalOrganization',
          name: SITE_NAME,
          url: BASE_URL
        },
        offers: {
          '@type': 'Offer',
          price: course.price,
          priceCurrency: course.currency || 'INR',
          availability: 'https://schema.org/InStock'
        },
        timeRequired: course.duration,
        educationalLevel: course.level,
        occupationalCredentialAwarded: 'Certificate'
      }
      addStructuredData('course-structured-data', courseData)
    }

    // Cleanup function
    return () => {
      removeStructuredData('page-structured-data')
      removeStructuredData('breadcrumb-structured-data')
      removeStructuredData('course-structured-data')
    }
  }, [fullTitle, description, keywords, image, url, type, author, noindex, canonical, structuredData, breadcrumbs, course])

  return null
}

// Helper functions
function updateMetaTag(name: string, content: string, attribute: 'name' | 'property' = 'name') {
  let element = document.querySelector(`meta[${attribute}="${name}"]`)
  
  if (element) {
    element.setAttribute('content', content)
  } else {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    element.setAttribute('content', content)
    document.head.appendChild(element)
  }
}

function updateCanonical(url: string) {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement
  
  if (link) {
    link.href = url
  } else {
    link = document.createElement('link')
    link.rel = 'canonical'
    link.href = url
    document.head.appendChild(link)
  }
}

function addStructuredData(id: string, data: object) {
  removeStructuredData(id)
  
  const script = document.createElement('script')
  script.id = id
  script.type = 'application/ld+json'
  script.textContent = JSON.stringify(data)
  document.head.appendChild(script)
}

function removeStructuredData(id: string) {
  const existing = document.getElementById(id)
  if (existing) {
    existing.remove()
  }
}

// Export utility for generating page-specific SEO data
export const SEOUtils = {
  generateCourseSchema: (course: {
    name: string
    description: string
    price: number
    duration: string
    level: string
    instructor?: string
  }) => ({
    '@context': 'https://schema.org',
    '@type': 'Course',
    name: course.name,
    description: course.description,
    provider: {
      '@type': 'EducationalOrganization',
      name: SITE_NAME,
      url: BASE_URL
    },
    offers: {
      '@type': 'Offer',
      price: course.price,
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock'
    },
    hasCourseInstance: {
      '@type': 'CourseInstance',
      courseMode: ['online', 'onsite'],
      instructor: {
        '@type': 'Person',
        name: course.instructor || 'Dr. Sudha M'
      }
    },
    educationalLevel: course.level,
    timeRequired: course.duration
  }),

  generateArticleSchema: (article: {
    title: string
    description: string
    image: string
    datePublished: string
    dateModified?: string
    author?: string
  }) => ({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: article.datePublished,
    dateModified: article.dateModified || article.datePublished,
    author: {
      '@type': 'Person',
      name: article.author || 'Dr. Sudha M'
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${BASE_URL}/Logo.png`
      }
    }
  }),

  generateLocalBusinessSchema: () => ({
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${BASE_URL}/#localbusiness`,
    name: SITE_NAME,
    image: `${BASE_URL}/Hero.png`,
    url: BASE_URL,
    telephone: '+91-XXXXXXXXXX',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Chennai',
      addressLocality: 'Chennai',
      addressRegion: 'Tamil Nadu',
      postalCode: '600001',
      addressCountry: 'IN'
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 13.0827,
      longitude: 80.2707
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
        opens: '09:00',
        closes: '18:00'
      }
    ],
    priceRange: '₹₹'
  })
}
